import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Mic,
  Logout,
  Dashboard,
  Login,
  PersonAdd,
  AdminPanelSettings,
} from '@mui/icons-material';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import ThemeSwitcher from './ThemeSwitcher';
import api from '../utils/api';

interface SubscriptionStatus {
  user_type: string;
  subscription_status?: string;
  trial_expires_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  is_trial_expired: boolean;
  meetings_count: number;
  trial_meetings_limit: number;
}

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  // Fetch subscription status when user is authenticated
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (isAuthenticated) {
        try {
          const response = await api.get('/subscription/status');
          setSubscriptionStatus(response.data);
        } catch (error) {
          console.error('Failed to fetch subscription status:', error);
          // Set fallback based on user type from auth state
          if (user?.user_type) {
            setSubscriptionStatus({
              user_type: user.user_type,
              is_trial_expired: false,
              meetings_count: 0,
              trial_meetings_limit: 5,
            });
          }
        }
      } else {
        setSubscriptionStatus(null);
      }
    };

    fetchSubscriptionStatus();
  }, [isAuthenticated, user]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDashboard = () => {
    navigate('/');
    setAnchorEl(null);
  };

  const handleAdmin = () => {
    navigate('/admin');
    setAnchorEl(null);
  };

  const isAdmin = user?.user_type === 'admin';

  // Helper function to get plan display name
  const getPlanDisplayName = (userType: string): string => {
    switch (userType.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'pro':
        return 'Pro';
      case 'paid':
        return 'Paid';
      case 'trial':
        return 'Trial';
      case 'pending':
        return 'Pending';
      default:
        return 'Free';
    }
  };

  // Helper function to get plan chip color and gradient
  const getPlanChipStyle = (userType: string) => {
    switch (userType.toLowerCase()) {
      case 'admin':
        return {
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          color: 'white',
        };
      case 'pro':
        return {
          background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
          color: 'white',
        };
      case 'paid':
        return {
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          color: 'white',
        };
      case 'trial':
        return {
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
        };
      case 'pending':
        return {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
        };
      default:
        return {
          background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
          color: 'white',
        };
    }
  };

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Mic />
            </Box>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              MeetingAI
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ThemeSwitcher />
          {isAuthenticated ? (
            <>
              {subscriptionStatus && (
                <Chip
                  label={getPlanDisplayName(subscriptionStatus.user_type)}
                  size="small"
                  sx={{
                    ...getPlanChipStyle(subscriptionStatus.user_type),
                    fontWeight: 600,
                    display: { xs: 'none', md: 'flex' },
                  }}
                />
              )}
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  p: 0,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 2,
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Signed in as
                  </Typography>
                  <Typography variant="body1" fontWeight={600} noWrap>
                    {user?.email || 'User'}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleDashboard}>
                  <Dashboard sx={{ mr: 1 }} />
                  Dashboard
                </MenuItem>
                {isAdmin && (
                  <>
                    <MenuItem onClick={handleAdmin}>
                      <AdminPanelSettings sx={{ mr: 1 }} />
                      Admin Panel
                    </MenuItem>
                    <MenuItem onClick={() => { navigate('/admin/dashboard'); setAnchorEl(null); }}>
                      <AdminPanelSettings sx={{ mr: 1 }} />
                      Admin Dashboard
                    </MenuItem>
                  </>
                )}
                <MenuItem onClick={() => { navigate('/pricing'); setAnchorEl(null); }} sx={{ gap: 1.5, py: 1 }}>
                  <PersonAdd fontSize="small" />
                  Pricing
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1, color: 'error.main' }}>
                  <Logout fontSize="small" />
                  Sign out
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                startIcon={<Login />}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  },
                }}
              >
                Sign In with Google
              </Button>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 